import React, { useEffect, useState, useMemo } from 'react';
import {
  feedService,
  FeedModel,
  FeedAction,
  FeedResponse,
  FeedLookupOrganisation,
  FeedLookupSubscription,
  FeedLookupUser,
} from '../../Services/feedService';
import { useToaster } from '../common/Toaster';
import { getErrorMessage } from '../../types/api';
import LoadingSpinner from '../common/LoadingSpinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardIconHeader,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Rss,
  Send,
  Trash2,
  Loader2,
  Link as LinkIcon,
  Smartphone,
  Monitor,
  CalendarClock,
  Bot,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Code2,
  AlertTriangle,
  Info,
  Plus,
  X,
  Zap,
} from 'lucide-react';

interface LastResponse {
  action: 'send' | 'delete';
  status: string;
  content: string;
  payload: string;
}

const Feed: React.FC = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [coPilotPrompt, setCoPilotPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { notify } = useToaster();

  // Type & Topic (Actions Required)
  const [feedType, setFeedType] = useState<'information' | 'activity'>('information');
  const [topicId, setTopicId] = useState('Access.NarcisEvo.General');
  const [actions, setActions] = useState<FeedAction[]>([]);

  // Link fields
  const [linkText, setLinkText] = useState('');
  const [linkDesktopUrl, setLinkDesktopUrl] = useState('');
  const [linkMobileUrl, setLinkMobileUrl] = useState('');

  // Due date
  const [dueDate, setDueDate] = useState('');

  // Response viewer
  const [lastResponse, setLastResponse] = useState<LastResponse | null>(null);
  const [showPayload, setShowPayload] = useState(false);

  // Lookup data
  const [organisations, setOrganisations] = useState<FeedLookupOrganisation[]>([]);
  const [lookupLoading, setLookupLoading] = useState(true);

  // Selected IDs
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedSubId, setSelectedSubId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    feedService.getLookupData()
      .then(data => {
        if (!controller.signal.aborted) {
          setOrganisations(data.organisations ?? []);
        }
      })
      .catch(err => {
        if (!controller.signal.aborted) {
          console.error('Failed to load feed lookup data:', err);
          notify('Error', 'Failed to load lookup data', 'danger');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLookupLoading(false);
      });

    return () => controller.abort();
  }, [notify]);

  // Derived data for cascading dropdowns
  const selectedOrg = useMemo(
    () => organisations.find(o => o.id === selectedOrgId),
    [organisations, selectedOrgId]
  );

  const subscriptions: FeedLookupSubscription[] = useMemo(
    () => selectedOrg?.subscriptions ?? [],
    [selectedOrg]
  );

  const selectedSub = useMemo(
    () => subscriptions.find(s => s.id === selectedSubId),
    [subscriptions, selectedSubId]
  );

  const users: FeedLookupUser[] = useMemo(
    () => selectedSub?.users ?? [],
    [selectedSub]
  );

  const selectedUser = useMemo(
    () => users.find(u => u.id === selectedUserId),
    [users, selectedUserId]
  );

  // Reset child selections when parent changes
  const handleOrgChange = (value: string) => {
    setSelectedOrgId(value);
    setSelectedSubId('');
    setSelectedUserId('');
  };

  const handleSubChange = (value: string) => {
    setSelectedSubId(value);
    setSelectedUserId('');
  };

  const clearForm = () => {
    setSelectedOrgId('');
    setSelectedSubId('');
    setSelectedUserId('');
    setTitle('');
    setDescription('');
    setFeedType('information');
    setTopicId('Access.NarcisEvo.General');
    setActions([]);
    setCoPilotPrompt('');
    setLinkText('');
    setLinkDesktopUrl('');
    setLinkMobileUrl('');
    setDueDate('');
  };

  const createFeedModel = (): FeedModel | null => {
    if (!selectedOrg || !selectedSub || !selectedUser) return null;

    const targetUserId = selectedUser.workspaceId || selectedUser.id;

    return {
      applicationId: selectedSub.applicationId,
      organisationId: selectedOrg.id,
      subscriptionId: selectedSub.id,
      userId: targetUserId,
      title,
      description,
      correlationId: `feed-${Date.now()}`,
      type: feedType,
      topicId: topicId || undefined,
      actions: actions.length > 0 ? actions : undefined,
      coPilotPrompt: coPilotPrompt || undefined,
      coPilotPromptAgent: coPilotPrompt ? 'access-evo-assistant' : undefined,
      linkText: linkText || undefined,
      linkDesktopUrl: linkDesktopUrl || undefined,
      linkMobileUrl: linkMobileUrl || undefined,
      dueDate: dueDate || undefined,
    };
  };

  const isFormValid = selectedOrgId && selectedSubId && selectedUserId && title && description;

  const isSuccess = (response: FeedResponse): boolean => {
    const statusCode = parseInt(response.resultStatus, 10);
    return response.resultStatus === 'OK' || response.resultStatus === 'Accepted' ||
      (!isNaN(statusCode) && statusCode >= 200 && statusCode < 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const feedModel = createFeedModel();
    if (!feedModel) return;

    setIsLoading(true);

    try {
      const response = await feedService.sendFeedItem(feedModel);

      setLastResponse({
        action: 'send',
        status: response.resultStatus,
        content: response.resultContent,
        payload: response.originalJsonPayload,
      });

      if (isSuccess(response)) {
        notify('Success', 'Feed item sent successfully!', 'success');
        clearForm();
      } else {
        notify('Error', `Feed send failed: ${response.resultStatus}`, 'danger');
      }
    } catch (error: unknown) {
      console.error('Feed send error:', error);
      const errorMessage = getErrorMessage(error);
      notify('Error', `Error sending feed item: ${errorMessage}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const feedModel = createFeedModel();
    if (!feedModel) {
      notify('Warning', 'Please fill in all required fields', 'warning');
      return;
    }

    setIsDeleting(true);

    try {
      const response = await feedService.deleteFeedItem(feedModel);

      setLastResponse({
        action: 'delete',
        status: response.resultStatus,
        content: response.resultContent,
        payload: response.originalJsonPayload,
      });

      if (isSuccess(response)) {
        notify('Success', 'Feed item deleted successfully!', 'success');
        clearForm();
      } else {
        notify('Error', `Feed delete failed: ${response.resultStatus}`, 'danger');
      }
    } catch (error: unknown) {
      console.error('Feed delete error:', error);
      const errorMessage = getErrorMessage(error);
      notify('Error', `Error deleting feed item: ${errorMessage}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatJson = (json: string): string => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  };

  const responseIsSuccess = lastResponse
    ? lastResponse.status === 'OK' || lastResponse.status === 'Accepted' ||
      (() => { const code = parseInt(lastResponse.status, 10); return !isNaN(code) && code >= 200 && code < 300; })()
    : false;

  if (lookupLoading) {
    return <LoadingSpinner label="Loading feed data..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardIconHeader icon={<Rss size={22} className="text-primary" />}>
          <CardTitle>Access Evo Feed</CardTitle>
          <CardDescription>
            Send feed items to the Access Workspace feed. Use <strong>Information</strong> type for general notifications
            or <strong>Activity</strong> type to create items that appear in Navigator's <strong>Actions Required</strong> section
            with inline actions, urgency ranking, and due dates.
          </CardDescription>
        </CardIconHeader>
      </Card>

      {/* Feed Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Create Feed Item</CardTitle>
          <CardDescription>
            Select a target organisation, subscription, and user, then compose the feed item.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Target Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organisation">Organisation *</Label>
                <Select value={selectedOrgId} onValueChange={handleOrgChange}>
                  <SelectTrigger id="organisation">
                    <SelectValue placeholder="Select organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {organisations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscription">Subscription *</Label>
                <Select
                  value={selectedSubId}
                  onValueChange={handleSubChange}
                  disabled={!selectedOrgId}
                >
                  <SelectTrigger id="subscription">
                    <SelectValue placeholder={selectedOrgId ? "Select subscription" : "Select organisation first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {subscriptions.map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>
                        {sub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user">Target User *</Label>
                <Select
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                  disabled={!selectedSubId}
                >
                  <SelectTrigger id="user">
                    <SelectValue placeholder={selectedSubId ? "Select user" : "Select subscription first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Auto-resolved IDs (read-only) */}
            {selectedSub && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-lg bg-muted/50 p-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Application ID</Label>
                  <p className="text-sm font-mono truncate" title={selectedSub.applicationId}>
                    {selectedSub.applicationId}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Organisation ID</Label>
                  <p className="text-sm font-mono truncate" title={selectedOrgId}>
                    {selectedOrgId}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {selectedUser?.workspaceId ? 'Workspace User ID' : 'User ID'}
                  </Label>
                  <p className="text-sm font-mono truncate" title={selectedUser?.workspaceId || selectedUser?.id}>
                    {selectedUser?.workspaceId || selectedUser?.id || '\u2014'}
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Enter feed item title"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dueDate" className="flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" />
                  Due Date <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="dueDate"
                  type="datetime-local"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description * <span className="text-muted-foreground font-normal text-xs">(supports markdown)</span></Label>
              <Textarea
                id="description"
                className="min-h-[100px]"
                placeholder="Enter a detailed description for the feed item"
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
              />
            </div>

            {/* Feed Type & Topic (Actions Required) */}
            <div className="space-y-3">
              <Label className="flex items-center gap-1.5 text-sm font-medium">
                <Zap className="h-4 w-4" />
                Feed Type & Navigator
              </Label>
              <div className="rounded-lg border p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="feedType" className="text-xs">Type</Label>
                    <Select value={feedType} onValueChange={(v) => setFeedType(v as 'information' | 'activity')}>
                      <SelectTrigger id="feedType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="information">
                          <span className="flex items-center gap-2">
                            <Info className="h-3.5 w-3.5 text-blue-500" />
                            Information
                          </span>
                        </SelectItem>
                        <SelectItem value="activity">
                          <span className="flex items-center gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                            Activity (Actions Required)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="topicId" className="text-xs">Topic ID</Label>
                    <Select value={topicId} onValueChange={setTopicId}>
                      <SelectTrigger id="topicId">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Access.NarcisEvo.General">Access.NarcisEvo.General</SelectItem>
                        <SelectItem value="Access.NarcisEvo.TaskApproval">Access.NarcisEvo.TaskApproval</SelectItem>
                        <SelectItem value="Access.NarcisEvo.ProjectUpdate">Access.NarcisEvo.ProjectUpdate</SelectItem>
                        <SelectItem value="Access.NarcisEvo.UserAction">Access.NarcisEvo.UserAction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {feedType === 'activity' && (
                  <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
                    <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        <strong>Activity</strong> feed items appear in Navigator's <strong>Actions Required</strong> section.
                        Users can action them inline without leaving Navigator. Add inline actions below to enable this.
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Inline Actions Builder */}
            {feedType === 'activity' && (
              <div className="space-y-3">
                <Label className="flex items-center gap-1.5 text-sm font-medium">
                  <Zap className="h-4 w-4" />
                  Inline Actions <span className="text-muted-foreground font-normal">(max 3)</span>
                </Label>
                <div className="rounded-lg border p-4 space-y-4">
                  {actions.map((action, index) => (
                    <div key={index} className="rounded-md border bg-muted/30 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Action {index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setActions(actions.filter((_, i) => i !== index))}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Action ID</Label>
                          <Input
                            placeholder="e.g. approve"
                            value={action.actionId}
                            onChange={e => {
                              const updated = [...actions];
                              updated[index] = { ...updated[index], actionId: e.target.value };
                              setActions(updated);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Button Text</Label>
                          <Input
                            placeholder="e.g. Approve"
                            value={action.text}
                            maxLength={15}
                            onChange={e => {
                              const updated = [...actions];
                              updated[index] = { ...updated[index], text: e.target.value };
                              setActions(updated);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Variant</Label>
                          <Select
                            value={action.variant}
                            onValueChange={v => {
                              const updated = [...actions];
                              updated[index] = { ...updated[index], variant: v };
                              setActions(updated);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="primary">Primary</SelectItem>
                              <SelectItem value="secondary">Secondary</SelectItem>
                              <SelectItem value="tertiary">Tertiary</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Backend ID</Label>
                          <Input
                            placeholder="e.g. narcisevo.feed"
                            value={action.backendId}
                            onChange={e => {
                              const updated = [...actions];
                              updated[index] = { ...updated[index], backendId: e.target.value };
                              setActions(updated);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">URI</Label>
                          <Input
                            placeholder="e.g. /api/feed/feedactions/approve"
                            value={action.uri}
                            onChange={e => {
                              const updated = [...actions];
                              updated[index] = { ...updated[index], uri: e.target.value };
                              setActions(updated);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">End Icon <span className="text-muted-foreground">(optional)</span></Label>
                          <Input
                            placeholder="e.g. CheckTickLined"
                            value={action.endIcon}
                            onChange={e => {
                              const updated = [...actions];
                              updated[index] = { ...updated[index], endIcon: e.target.value };
                              setActions(updated);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {actions.length < 3 && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setActions([...actions, {
                          actionId: '',
                          text: '',
                          variant: 'primary',
                          backendId: 'narcisevo.feed',
                          uri: '/api/feed/feedactions/',
                          endIcon: '',
                        }])}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Action
                      </Button>
                      {actions.length === 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-muted-foreground"
                          onClick={() => setActions([
                            { actionId: 'approve', text: 'Approve', variant: 'primary', backendId: 'narcisevo.feed', uri: '/api/feed/feedactions/approve', endIcon: 'CheckTickLined' },
                            { actionId: 'reject', text: 'Reject', variant: 'secondary', backendId: 'narcisevo.feed', uri: '/api/feed/feedactions/reject', endIcon: 'CircleCrossLined' },
                          ])}
                        >
                          Use Template (Approve/Reject)
                        </Button>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Actions appear as buttons on the feed item. The BFF proxy calls the URI on your backend when a user clicks an action.
                    Max 15 characters per button text. Render order: tertiary, secondary, primary (left to right).
                  </p>
                </div>
              </div>
            )}

            {/* Links Section */}
            <div className="space-y-3">
              <Label className="flex items-center gap-1.5 text-sm font-medium">
                <LinkIcon className="h-4 w-4" />
                Feed Item Link <span className="text-muted-foreground font-normal">(Optional)</span>
              </Label>
              <div className="rounded-lg border p-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="linkText" className="text-xs">Link Text</Label>
                  <Input
                    id="linkText"
                    placeholder="e.g. View more in your app"
                    value={linkText}
                    onChange={e => setLinkText(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="linkDesktopUrl" className="flex items-center gap-1.5 text-xs">
                      <Monitor className="h-3 w-3" /> Desktop URL
                    </Label>
                    <Input
                      id="linkDesktopUrl"
                      type="url"
                      placeholder="https://your-app.com/desktop"
                      value={linkDesktopUrl}
                      onChange={e => setLinkDesktopUrl(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkMobileUrl" className="flex items-center gap-1.5 text-xs">
                      <Smartphone className="h-3 w-3" /> Mobile URL
                    </Label>
                    <Input
                      id="linkMobileUrl"
                      type="url"
                      placeholder="https://your-app.com/mobile"
                      value={linkMobileUrl}
                      onChange={e => setLinkMobileUrl(e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Provide both desktop and mobile URLs so users on any device can navigate back to your product.
                  If both URLs are the same, enter the same URL in both fields.
                </p>
              </div>
            </div>

            {/* Copilot Prompt */}
            <div className="space-y-2">
              <Label htmlFor="coPilotPrompt" className="flex items-center gap-1.5">
                <Bot className="h-3.5 w-3.5" />
                Copilot Prompt <span className="text-muted-foreground font-normal">(Optional)</span>
              </Label>
              <Input
                id="coPilotPrompt"
                placeholder="Enter a Copilot prompt to suggest an AI action"
                value={coPilotPrompt}
                onChange={e => setCoPilotPrompt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Attach a Copilot prompt to the feed item so users can interact with AI directly from the feed.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 justify-between pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={clearForm}
                disabled={isLoading || isDeleting}
              >
                Clear
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isLoading || isDeleting || !isFormValid}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {isDeleting ? 'Deleting...' : 'Delete Feed Item'}
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || isDeleting || !isFormValid}
                  className="gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {isLoading ? 'Sending...' : 'Send Feed Item'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Response Viewer */}
      {lastResponse && (
        <Card className="animate-fade-in-up">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {responseIsSuccess ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive" />
              )}
              Last {lastResponse.action === 'send' ? 'Send' : 'Delete'} Result
            </CardTitle>
            <CardDescription>
              Status: <span className={responseIsSuccess ? 'text-emerald-600 font-medium' : 'text-destructive font-medium'}>
                {lastResponse.status}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lastResponse.content && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Response Content</Label>
                <pre className="rounded-lg bg-muted/50 p-3 text-xs font-mono overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap break-words">
                  {formatJson(lastResponse.content)}
                </pre>
              </div>
            )}

            {lastResponse.payload && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPayload(!showPayload)}
                >
                  <Code2 className="h-3.5 w-3.5" />
                  JSON Payload Sent
                  {showPayload ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
                {showPayload && (
                  <pre className="rounded-lg bg-muted/50 p-3 text-xs font-mono overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap break-words">
                    {formatJson(lastResponse.payload)}
                  </pre>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Feed;
